<template>
  <div>
    <sdui-search
      v-if="isSelectionMode && absenceTypes.length"
      v-model="searchAbsenceType"
      :placeholder="$t('global.search')"
      :input-label-sr-only-text="$t('management.abscence_categories.search_category')"
      class="mt-n3 mb-4 pb-2 pt-2"
    />

    <div :class="{ 'px-1': isSelectionMode }" data-cy="absence-categories-list">
      <SList v-if="immutableAbsenceTypes.length">
        <SListItem
          v-for="(absenceType, key) in immutableAbsenceTypes"
          :key="key"
          :title="absenceType.meta.name"
          :class="{ 'is-selected': !isSelectionMode && isAbsenceTypeSelected(absenceType) }"
          :description="absenceType.meta.description"
          :data-cy="`immutable-absence-${key}`"
          icon="icon-help"
          :aria-pressed="isFilterMode ? (absenceType.selected ? 'true' : 'false') : undefined"
          @click.prevent="selectAbsenceType(absenceType)"
        >
          <template #right-action v-if="isFilterMode">
            <SCheckbox v-model="absenceType.selected"
                       name="type-selected"
                       :label-text="absenceType.meta.name"
            />
          </template>
        </SListItem>
      </SList>
      <p v-if="!isSelectionMode" class="gray-300 nano mt-2 mb-4 pb-2">{{ $t('management.abscence_categories.standard_category_hint') }}</p>

      <sdui-loader v-if="$wait.waiting('fetch-absence-categories')" />

      <template v-else>
        <!--using v-show instead of v-if because the element needs to be in the dom in order to toggle the accordion-->
        <SList v-show="showGroups && !searchAbsenceType.length">
          <SListItem
            v-if="!isSelectionMode"
            icon="icon-add-o-24"
            class="add-category"
            primary-text-color
            :title="$t('management.abscence_categories.add_category')"
            @click.stop="addAbsenceCategoryItem()"
          />
          <SListItem
            v-for="(groupedAbsence, key) in groupedAbsences"
            :key="key"
            class="accordion-first-level"
            :data-cy="`accordion-toggle-${groupedAbsence.groupKey}`"
            :title="groupedAbsence.category && groupedAbsence.category.name"
            :icon="groupedAbsence.category.icon ? `icon-${groupedAbsence.category.icon}` : ''"
            icon-color="gray-300 secondary-bg"
            :right-action-icon="isFilterMode ? undefined : 'icon-chevron-down'"
            :aria-expanded="currentlyExpanded === groupedAbsence.groupKey ? 'true' : 'false'"
            :aria-disabled="currentlyExpanded === groupedAbsence.groupKey"
            :aria-controls="'accordion-' + groupedAbsence.groupKey"
            @click="toggle(groupedAbsence.groupKey)"
          >
            <template #collapsable>
              <b-collapse
                :id="'accordion-' + groupedAbsence.groupKey"
                :data-cy="'accordion-' + groupedAbsence.groupKey"
                :accordion="isFilterMode ? '' : 'absence-cat-accordion'"
                :visible="isFilterMode"
              >
                <SList key="slist" class="my-2 mx-2">
                  <absence-category-accordion-second-level
                    v-for="(groupedAbsenceType, key) in groupedAbsence.absenceTypes"
                    :key="key"
                    :absence-type="groupedAbsenceType"
                    :absence-categories="absenceCategories"
                    :is-selection-mode="isSelectionMode"
                    :is-filter-mode="isFilterMode"
                    :is-absence-type-selected="isAbsenceTypeSelected"
                    :data-cy="`group-absence-types-${key}`"
                    @select-absence-type="selectAbsenceType"
                  />
                </SList>
              </b-collapse>
            </template>
          </SListItem>
        </SList>
        <!-- using v-if here because v-show-else doesnt exist -->
        <SList v-if="!showGroups || searchAbsenceType.length">
          <absence-category-accordion-second-level
            v-for="(groupedAbsence, key) in groupedAbsences"
            :key="key"
            :absence-type="groupedAbsence"
            :absence-categories="absenceCategories"
            :is-selection-mode="isSelectionMode"
            :is-filter-mode="isFilterMode"
            :is-absence-type-selected="isAbsenceTypeSelected"
            @select-absence-type="selectAbsenceType"
          />
        </SList>

        <div v-if="isSelectionMode && !isFilterMode" class="mt-2 text-muted f-12 font-weight-light text-center">
          <div v-if="showGroups">
            {{ $t('management.abscence_categories.modal.hint_teacher') }}
          </div>
          <div v-else>
            {{ $t('management.abscence_categories.modal.hint_student_parent') }}
          </div>
        </div>

        <SModalPortalTo v-if="isFilterMode">
          <b-button variant="primary"
                    data-cy="submit-filter"
                    type="button"
                    size="lg"
                    block
                    @click="submitNavigateBack()"
          >
            <span>{{ $t('management.abscence_categories.modal.submit_selected_reasons', { nbr: countSelectedTypes }) }}</span>
          </b-button>
        </SModalPortalTo>
      </template>
    </div>
  </div>
</template>

<script>
import SduiSearch from 'src/components/miscellaneous/ui/forms/SduiSearch.vue'
import AbsenceCategoryAccordionSecondLevel from 'src/components/classbook/absences/AbsenceCategoryAccordionSecondLevel.vue'
import SduiLoader from 'src/components/miscellaneous/ui/common/SduiLoader.vue'
import constants from 'src/config/constants.ts'
import isMobileDeviceMixing from 'src/mixins/miscellaneous/isMobileDeviceMixin.js'

import SCheckbox from 'src/components/base/SCheckbox.vue'
import SModalPortalTo from 'src/components/base/SModal/SModalPortalTo.vue'

export default {
  name: 'AbsenceCategorySelection',
  components: { SModalPortalTo, SduiSearch, SduiLoader, AbsenceCategoryAccordionSecondLevel, SCheckbox },
  mixins: [isMobileDeviceMixing],
  props: {
    absenceTypes: {
      type: Array,
      default: () => [],
    },

    absenceCategories: {
      type: Object,
      default: () => ({}),
    },

    isSelectionMode: {
      type: Boolean,
      default: false,
    },

    isFilterMode: {
      type: Boolean,
      default: false,
    },

    isAbsenceTypeSelected: {
      type: Function,
      default: () => false,
    },

    showGroups: {
      type: Boolean,
      default: true,
    },

    selectedAbsenceTypes: {
      type: Array,
      default: () => [],
    },
  },

  data: () => ({
    searchAbsenceType: '',
    classbookId: null,
    localSelectedAbsenceTypes: [],
    currentlyExpanded: null,
  }),

  computed: {
    mappedSelectedAbsenceTypes() {
      if (!this.isFilterMode) return
      return this.absenceTypes.map((el) => ({
        ...el,
        selected: !!this.localSelectedAbsenceTypes.find((selectedEl) => el.id === selectedEl.id)?.selected,
      }))
    },

    immutableAbsenceTypes() {
      // absence type which cant be modified by user
      if (!this.isFilterMode) return this.absenceTypes.filter((el) => this.filter(el, true))
      return this.mappedSelectedAbsenceTypes.filter((el) => this.filter(el, true))
    },

    groupedAbsences() {
      if (!this.absenceTypes.length || !Object.keys(this.absenceCategories).length) return []
      const mutableAbsenceTypes = this.isFilterMode
        ? this.mappedSelectedAbsenceTypes.filter((el) => this.filter(el, false))
        : this.absenceTypes.filter((el) => this.filter(el, false))

      if (!this.showGroups || this.searchAbsenceType.length) return mutableAbsenceTypes

      return this.getGroupedAbsenceCategories(mutableAbsenceTypes)
    },

    absenceTypeId() {
      return Number(this.$route.params?.absenceTypeId) || 0
    },

    countSelectedTypes() {
      return this.localSelectedAbsenceTypes.filter((el) => el.selected).length
    },
  },

  emits: ['absence-type-selected', 'update-filter', 'start-create'],

  methods: {
    getGroupedAbsenceCategories(mutableAbsenceTypes) {
      const showCategory = (category) => {
        return (
          category !== constants.classbook.absences.types.STANDARD_TYPE_ID &&
          (!this.isSelectionMode || (this.isSelectionMode && mutableAbsenceTypes.find((el) => el.category === category)))
        )
      }

      return Object.keys(this.absenceCategories)
        .filter((el) => showCategory(el))
        .map((key) => ({
          groupKey: key,
          absenceTypes: mutableAbsenceTypes.filter((el) => el.category === key),
          category: this.absenceCategories[key],
        }))
    },

    selectAbsenceType(absenceType) {
      if (this.isFilterMode) return this.updateSelectedAbsenceType(absenceType)
      return this.$emit('absence-type-selected', absenceType)
    },

    updateSelectedAbsenceType(absenceType) {
      const isSelected = !absenceType.selected

      // if selected, add to array otherwise delete it
      this.localSelectedAbsenceTypes = isSelected
        ? [...this.localSelectedAbsenceTypes, { ...absenceType, selected: !absenceType.selected }]
        : [...this.localSelectedAbsenceTypes.filter((el) => el.id !== absenceType.id)]
    },

    addAbsenceCategoryItem() {
      this.$emit('start-create')
    },

    isExpanded(accordionKey) {
      const accordionEl = document.getElementById(accordionKey)
      if (!accordionEl) return false
      if (accordionEl.className.toLowerCase().includes('collapse show')) return true
    },

    toggle(key) {
      if (this.isFilterMode) return
      // do not toggle twice, otherwise the element gets collapsed. Elements are only collapsed by expanding other elements in the list
      if (this.isExpanded(`accordion-${key}`)) return
      this.currentlyExpanded = key
      this.$root.$emit('bv::toggle::collapse', `accordion-${key}`)
    },

    filter(el, onlyImmutable) {
      const categoryFilter = onlyImmutable
        ? el.category === constants.classbook.absences.types.STANDARD_TYPE_ID
        : el.category !== constants.classbook.absences.types.STANDARD_TYPE_ID
      if (!this.searchAbsenceType.length) return categoryFilter
      return categoryFilter && el.meta.name.toLowerCase().indexOf(this.searchAbsenceType.toLowerCase()) > -1
    },

    submitNavigateBack() {
      this.$emit('update-filter', null, this.localSelectedAbsenceTypes, true)
    },
  },

  watch: {
    groupedAbsences: {
      handler: async function (val) {
        // this handler toggles the accordion (is active if => this.showGroups) to the default absenceTypeOnOpen
        // if the user is searching (this.searchAbsenceType.length) there are no groups shown => no toggle
        if (!this.showGroups || !val.length || this.searchAbsenceType.length) return
        // if no id was given, toggle accordion for the first absenceType of the first category
        const absenceTypeOnOpen =
          this.absenceTypeId === 0 ? val[0].absenceTypes[0] : this.absenceTypes.find((el) => el.id === this.absenceTypeId)
        await this.$nextTick()
        this.toggle(absenceTypeOnOpen?.category)
      },

      immediate: true,
      deep: true,
    },

    selectedAbsenceTypes: {
      handler(val) {
        if (!Object.keys(val).length) return
        this.localSelectedAbsenceTypes = [...this.localSelectedAbsenceTypes, ...val]
      },

      immediate: true,
      deep: true,
    },
  },
}
</script>

<style scoped lang="scss">
.collapse {
  border-bottom: 1px solid $color-gray-100;
}

.is-selected {
  position: relative;
}
.is-selected::before {
  content: '';
  width: 2px;
  height: 100%;
  background-color: $color-primary;
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
}
</style>
